import { Button, ButtonGroup, Grid, Tooltip, styled } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useMemo } from 'react';
import PropType from 'prop-types';
import { useTranslation } from 'react-i18next';

const ViewModesWrapper = styled(Grid)(({ theme }) => ({
  gap: theme.spacing(1),
  fontWeight: theme.typography.fontWeightBold,
  marginTop: theme.spacing(2)
}));

const storeViewMode = (value, cacheKey) => localStorage.setItem(cacheKey, value);
const retrieveViewMode = cacheKey => localStorage.getItem(cacheKey);

const ViewModes = React.memo(({ viewModes, setViewModes, cacheKey }) => {
  const { t } = useTranslation();

  const handleSelect = useCallback(
    name => () => {
      setViewModes(prev => prev.map(x => (x.name === name ? { ...x, selected: true } : { ...x, selected: false })));
      cacheKey && storeViewMode(name, cacheKey);
    },
    [setViewModes, cacheKey]
  );

  return (
    <ViewModesWrapper item container justifyContent='flex-end' alignItems='center'>
      {t('General.ViewMode')}
      <ButtonGroup variant='text' color='primary'>
        {viewModes.map((viewMode, index) => (
          <Tooltip key={index} title={viewMode.caption}>
            <Button variant={viewMode.selected ? 'contained' : 'outlined'} onClick={handleSelect(viewMode.name)}>
              <viewMode.icon fontSize='small' />
            </Button>
          </Tooltip>
        ))}
      </ButtonGroup>
    </ViewModesWrapper>
  );
});

ViewModes.displayName = 'ViewModes';

ViewModes.propTypes = {
  viewModes: PropType.array.isRequired,
  setViewModes: PropType.func.isRequired,
  cacheKey: PropType.string
};

const useViewModes = (list, cacheKey) => {
  const initializedViewModes = useMemo(() => {
    if (!list || list.length === 0) return [];

    const storedViewMode = retrieveViewMode(cacheKey);
    const _list = list.map(x => ({ ...x, selected: x.selected ?? x.name === storedViewMode }));

    if (!_list.some(x => x.selected)) {
      _list[0].selected = true;
      cacheKey && storeViewMode(_list[0].name, cacheKey);
    }

    return _list;
  }, [list, cacheKey]);

  const [viewModes, setViewModes] = useState(initializedViewModes);
  const selectedMode = useMemo(() => viewModes.find(x => x.selected), [viewModes]);

  return {
    ViewModesComponent: () => <ViewModes viewModes={viewModes} setViewModes={setViewModes} cacheKey={cacheKey} />,
    Components: selectedMode.components
  };
};

export default useViewModes;
